import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { useMemo } from "react";

import { ContentContainer, PageLayout, PageTitle } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useGetPriceRemarkLabelAuditQuery } from "@/services/price-remark/monitoring";
import { LabelAuditRow } from "@/services/price-remark/types";
import { formatToBRL } from "@/utils/formatToMoney";

import { PriceRemarkFilter } from "../../components/filter";
import { KpiCards } from "../../components/kpi-cards";
import { QueryErrorAlert } from "../../components/query-error-alert";
import { ColumnHeader } from "../../components/table/column-header";
import { MonitoringTable } from "../../components/table/monitoring-table";
import { ServerPagination } from "../../components/table/server-pagination";
import { usePriceRemarkFilterActions } from "../../ducks/filter/hooks/actions";
import { usePriceRemarkFilterState } from "../../ducks/filter/hooks/filterState";
import { exportRowsToXlsx } from "../../utils/export-xlsx";
import { extractQueryErrorMessage } from "../../utils/query-error";
import { useExportLabelAudit } from "../hooks/use-export-label-audit";

const columns: ColumnDef<LabelAuditRow>[] = [
  {
    accessorKey: "emitida_em",
    header: ({ column }) => <ColumnHeader column={column} title="Emitida em" />,
    meta: { label: "Emitida em" },
  },
  {
    accessorKey: "sku",
    header: ({ column }) => <ColumnHeader column={column} title="SKU" />,
    meta: { label: "SKU" },
  },
  {
    accessorKey: "descricao",
    header: "Descrição",
    meta: { label: "Descrição" },
    cell: ({ row }) => (
      <span
        className="block max-w-[280px] truncate"
        title={row.original.descricao ?? undefined}
      >
        {row.original.descricao ?? "—"}
      </span>
    ),
  },
  { accessorKey: "tipo", header: "Tipo", meta: { label: "Tipo" } },
  { accessorKey: "qtd", header: "Qtd", meta: { label: "Qtd" } },
  {
    accessorKey: "status",
    header: ({ column }) => <ColumnHeader column={column} title="Status" />,
    meta: { label: "Status" },
  },
  { accessorKey: "operador", header: "Operador", meta: { label: "Operador" } },
  {
    accessorKey: "preco_na_etiqueta",
    header: () => <div className="text-right">Preço etiqueta</div>,
    meta: { label: "Preço etiqueta", cellClassName: "text-right" },
    cell: ({ row }) =>
      row.original.preco_na_etiqueta == null
        ? "—"
        : formatToBRL(row.original.preco_na_etiqueta),
  },
  {
    accessorKey: "vigente_ate",
    header: "Vigente até",
    meta: { label: "Vigente até" },
    cell: ({ row }) => row.original.vigente_ate ?? "—",
  },
  {
    accessorKey: "confere",
    header: ({ column }) => <ColumnHeader column={column} title="Confere" />,
    meta: { label: "Confere" },
    cell: ({ row }) => (
      <span
        className={
          row.original.confere === "OK"
            ? "text-green-600"
            : "font-medium text-amber-600"
        }
      >
        {row.original.confere}
      </span>
    ),
  },
  {
    accessorKey: "vigencia",
    header: "Vigência",
    meta: { label: "Vigência" },
    cell: ({ row }) => (
      <span
        className={
          row.original.vigencia === "VIGENTE"
            ? "text-green-600"
            : "text-red-600"
        }
      >
        {row.original.vigencia}
      </span>
    ),
  },
];

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={active ? "default" : "outline"}
      size="sm"
      className="h-7"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function PriceRemarkLabelAudit() {
  const { filters } = usePriceRemarkFilterState();
  const { updateFilter } = usePriceRemarkFilterActions();
  const { exportAll, isExporting } = useExportLabelAudit();
  const hasStore = filters.loja != null;

  const { data, isFetching, isError, error, refetch } =
    useGetPriceRemarkLabelAuditQuery(
      {
        loja: filters.loja ?? 0,
        dtIni: filters.dtIni,
        dtFim: filters.dtFim,
        page: filters.page,
        perPage: filters.perPage,
        tipo: filters.tipo,
        status: filters.status,
        confere: filters.confere,
        vigencia: filters.vigencia,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      },
      { skip: !hasStore },
    );

  const rows = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;

  const setQuick = (key: "confere" | "vigencia", value: string | null) => {
    updateFilter({ key, value });
    updateFilter({ key: "page", value: 1 });
  };

  const sorting = useMemo<SortingState>(
    () =>
      filters.sortBy
        ? [{ id: filters.sortBy, desc: filters.sortOrder !== "asc" }]
        : [],
    [filters.sortBy, filters.sortOrder],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const first = next[0];
    updateFilter({ key: "sortBy", value: first?.id ?? null });
    updateFilter({
      key: "sortOrder",
      value: first ? (first.desc ? "desc" : "asc") : null,
    });
    updateFilter({ key: "page", value: 1 });
  };

  const kpis = useMemo(() => {
    const desatualizadas = rows.filter((r) => r.confere === "DESATUALIZADA").length;
    const expiradas = rows.filter((r) => r.vigencia === "EXPIRADA").length;
    return [
      { label: "Total (filtro)", value: total },
      { label: "Nesta página", value: rows.length },
      {
        label: "Desatualizadas (pág.)",
        value: desatualizadas,
        tone: "warning" as const,
        hint: "use 'Só desatualizadas' p/ o total",
      },
      {
        label: "Expiradas (pág.)",
        value: expiradas,
        tone: "danger" as const,
      },
    ];
  }, [rows, total]);

  return (
    <PageLayout>
      <PageTitle
        title="Auditoria da etiqueta"
        description="Rastro item a item; o 'preço de' é o preço normal vigente hoje. Loja e período obrigatórios."
      />
      <ContentContainer>
        <div className="space-y-4">
          <PriceRemarkFilter showStore storeRequired showTipo showStatus />

          {hasStore && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">Confere:</Label>
                <Chip active={!filters.confere} onClick={() => setQuick("confere", null)}>
                  Todas
                </Chip>
                <Chip
                  active={filters.confere === "DESATUALIZADA"}
                  onClick={() => setQuick("confere", "DESATUALIZADA")}
                >
                  Só desatualizadas
                </Chip>
                <Chip
                  active={filters.confere === "OK"}
                  onClick={() => setQuick("confere", "OK")}
                >
                  Só OK
                </Chip>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">Vigência:</Label>
                <Chip active={!filters.vigencia} onClick={() => setQuick("vigencia", null)}>
                  Todas
                </Chip>
                <Chip
                  active={filters.vigencia === "EXPIRADA"}
                  onClick={() => setQuick("vigencia", "EXPIRADA")}
                >
                  Só expiradas
                </Chip>
                <Chip
                  active={filters.vigencia === "VIGENTE"}
                  onClick={() => setQuick("vigencia", "VIGENTE")}
                >
                  Só vigentes
                </Chip>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={rows.length === 0}
                  onClick={() =>
                    exportRowsToXlsx(
                      rows as unknown as Record<string, unknown>[],
                      `auditoria-etiqueta-loja-${filters.loja}-pagina-${filters.page}`,
                    )
                  }
                >
                  <Download className="mr-1 h-4 w-4" /> Exportar página (xlsx)
                </Button>
                <Button
                  size="sm"
                  className="h-8"
                  disabled={total === 0 || isExporting}
                  onClick={() =>
                    exportAll({
                      loja: filters.loja ?? 0,
                      dtIni: filters.dtIni,
                      dtFim: filters.dtFim,
                      tipo: filters.tipo,
                      status: filters.status,
                      confere: filters.confere,
                      vigencia: filters.vigencia,
                      sortBy: filters.sortBy,
                      sortOrder: filters.sortOrder,
                    })
                  }
                >
                  <Download className="mr-1 h-4 w-4" />
                  {isExporting
                    ? "Solicitando..."
                    : `Enviar por e-mail (${total} linhas)`}
                </Button>
              </div>
            </div>
          )}

          {!hasStore ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
              Selecione uma loja para consultar o rastro das etiquetas.
            </div>
          ) : isError ? (
            <QueryErrorAlert
              message={extractQueryErrorMessage(error)}
              onRetry={refetch}
            />
          ) : (
            <>
              <KpiCards items={kpis} />
              <MonitoringTable
                columns={columns}
                data={rows}
                isLoading={isFetching}
                hidePagination
                searchPlaceholder="Buscar SKU/descrição/operador (nesta página)..."
                emptyMessage="Nenhuma etiqueta no período para esta loja."
                sorting={sorting}
                onSortingChange={handleSortingChange}
              />
              <ServerPagination
                page={filters.page}
                perPage={filters.perPage}
                total={total}
                onPageChange={(page) => updateFilter({ key: "page", value: page })}
                onPerPageChange={(perPage) => {
                  updateFilter({ key: "perPage", value: perPage });
                  updateFilter({ key: "page", value: 1 });
                }}
              />
            </>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
}

export default PriceRemarkLabelAudit;
